import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import cormorant400 from "@fontsource/cormorant-garamond/files/cormorant-garamond-latin-400-normal.woff2?url";
import cormorant400i from "@fontsource/cormorant-garamond/files/cormorant-garamond-latin-400-italic.woff2?url";
import inter400 from "@fontsource/inter/files/inter-latin-400-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Presença Que Marca — Homens que deixam marcas eternas" },
      { name: "description", content: "Fé, família e legado. Uma missão para homens cristãos que desejam viver com presença, propósito e influência." },
      { name: "author", content: "Presença Que Marca" },
      { property: "og:title", content: "Presença Que Marca — Homens que deixam marcas eternas" },
      { property: "og:description", content: "Fé, família e legado. Uma missão para homens cristãos que desejam viver com presença, propósito e influência." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Presença Que Marca" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Presença Que Marca — Homens que deixam marcas eternas" },
      { name: "twitter:description", content: "Fé, família e legado. Uma missão para homens cristãos que desejam viver com presença, propósito e influência." },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "preconnect", href: "https://pay.hotmart.com" },
      { rel: "preload", as: "font", type: "font/woff2", href: cormorant400, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: cormorant400i, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: inter400, crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "dns-prefetch", href: "https://connect.facebook.net" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        {/* Google Tag Manager (GTM-K5Z8D3Q9) — dataLayer disponível de imediato,
            script carregado após load/idle, primeira interação ou 2,5s. */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var loaded=false;var events=['scroll','pointerdown','keydown','touchstart'];function load(){if(loaded)return;loaded=true;events.forEach(function(e){w.removeEventListener(e,load)});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f)}events.forEach(function(e){w.addEventListener(e,load,{once:true,passive:true})});function schedule(){('requestIdleCallback' in w?w.requestIdleCallback:w.setTimeout)(load,{timeout:2500});w.setTimeout(load,2500)}if(d.readyState==='complete'){schedule()}else{w.addEventListener('load',schedule,{once:true})}})(window,document,'script','dataLayer','GTM-K5Z8D3Q9');`,
          }}
        />
        {/* End Google Tag Manager */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(f,b,e,v){if(f.fbq)return;var n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];var loaded=false;var events=['scroll','pointerdown','keydown','touchstart'];function load(){if(loaded)return;loaded=true;events.forEach(function(x){f.removeEventListener(x,load)});var t=b.createElement(e);t.async=!0;t.src=v;var s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}events.forEach(function(x){f.addEventListener(x,load,{once:true,passive:true})});function schedule(){('requestIdleCallback' in f?f.requestIdleCallback:f.setTimeout)(load,{timeout:2500});f.setTimeout(load,2500)}if(b.readyState==='complete'){schedule()}else{f.addEventListener('load',schedule,{once:true})}fbq('init','1873919169968232');fbq('track','PageView')})(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');`,
          }}
        />
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=1873919169968232&ev=PageView&noscript=1"
          />
        </noscript>
      </head>
      <body>
        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-K5Z8D3Q9"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {/* End Google Tag Manager (noscript) */}
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
    </QueryClientProvider>
  );
}
